import React, { useEffect, useRef, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Gift,
  Info,
  CheckCircle,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { useGlobal } from "../../context/GlobalContext";

const RedeemDetails = () => {
  const { getRewardDetails, redeemReward } = useGlobal();
  const navigate = useNavigate();
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const location = useLocation();
  const [details, setDetails] = useState({});

  const product_pid = location.state?.product_pid;

  useEffect(() => {
    const load = async () => {
      const data = await getRewardDetails(product_pid);
      setDetails(data || {});
    };
    load();
  }, [product_pid]);

  const redeemData = {
    images: details?.product_images || [],
    name: details?.product?.name || "",
    desc: details?.product?.description || "",
    redeemPts: details?.product?.points || 0,
    userBalance: details?.user_reward_points?.balance || 0,
  };

  const canRedeem = redeemData.userBalance >= redeemData.redeemPts;

  return (
    <div className="content-part2 rd-page">
      <div className="row g-4 align-items-start">
        <div className="col-12 col-md-6">
          <div className="rd-slider">
            <button ref={prevRef} className="rd-nav-btn rd-nav-btn--left">
              <ChevronLeft size={18} />
            </button>
            <button ref={nextRef} className="rd-nav-btn rd-nav-btn--right">
              <ChevronRight size={18} />
            </button>
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={0}
              slidesPerView={1}
              loop={true}
              pagination={{ clickable: true }}
              autoplay={{ delay: 2800, disableOnInteraction: false }}
              onBeforeInit={(swiper) => {
                swiper.params.navigation.prevEl = prevRef.current;
                swiper.params.navigation.nextEl = nextRef.current;
              }}
              className="rd-swiper"
            >
              {redeemData.images.map((img, i) => (
                <SwiperSlide key={i}>
                  <div className="rd-slide">
                    <img src={img} alt={`${redeemData.name} ${i + 1}`} />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>

        <div className="col-12 col-md-6">
          <div className="rd-detail">
            {/* Category + name */}
            <h4 className="rd-name">{redeemData.name}</h4>

            {/* Description */}
            <div className="rd-desc-card">
              <div className="rd-desc-header">
                <Info size={14} />
                About this item
              </div>
              <p className="rd-desc-text">{redeemData.desc}</p>
            </div>

            {/* Redeem action */}
            <div className="rd-action-card">
              <div className="rd-action-top">
                <div>
                  <div className="rd-pts-label">Points required</div>
                  <div className="rd-pts-value">
                    <Gift size={22} className="rd-pts-icon" />
                    <span className="rd-pts-num">{redeemData.redeemPts}</span>
                    <small className="rd-pts-unit">pts</small>
                  </div>
                </div>
                <div className="rd-balance-box">
                  <div className="rd-balance-label">Your balance</div>
                  <div className="rd-balance-num">
                    {redeemData.userBalance} pts
                  </div>
                </div>
              </div>

              <div
                className={`rd-status ${canRedeem ? "rd-status--ok" : "rd-status--low"}`}
              >
                <CheckCircle size={14} />
                {canRedeem
                  ? "You have enough points to redeem this item."
                  : `You need ${redeemData.redeemPts - redeemData.userBalance} more points.`}
              </div>

              <button
                className="btn btn-secondary d-flex align-items-center justify-content-center gap-2"
                disabled={!canRedeem}
                onClick={async () => {
                  const result = await redeemReward(product_pid);

                  if (result?.status) {
                    setTimeout(() => {
                      navigate("/points-history");
                    }, 1500);
                  }
                }}
              >
                <Gift size={15} />
                Confirm Redeem
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RedeemDetails;
